import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { FaExternalLinkAlt, FaCheck, FaTimes, FaCalendarAlt, FaMoneyBillWave, FaCogs, FaChartLine } from "react-icons/fa";
import projects from "../../Data/Projects.js"; // Adjust the import path as necessary

const ProjectDetails = () => {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);

  if (!project) {
    return <div className="p-8 text-center text-xl text-white">Project not found</div>;
  }

  // Breadcrumb schema (Google verified)
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: project.breadcrumb.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: `${window.location.origin}${crumb.url}`
    }))
  };

  // Project schema for structured data
  const projectSchema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.title,
    url: project.url,
    description: project.description,
    creator: {
      "@type": "Organization",
      name: "Mern Digital",
      url: window.location.origin
    },
    dateCreated: project.timelineForCompletion?.start,
    datePublished: project.timelineForCompletion?.end,
    keywords: project.tags?.join(", "),
    image: project.gallery?.map((img) => `${window.location.origin}${img}`)
  };

  return (
    <>
      <Helmet>
        <title>{project.seo?.metaTitle}</title>
        <meta name="description" content={project.seo?.metaDescription} />
        <meta name="keywords" content={project.seo?.keywords?.join(", ")} />
        <meta property="og:title" content={project.seo?.metaTitle} />
        <meta property="og:description" content={project.seo?.metaDescription} />
        {project.seo?.ogImage && (
          <meta property="og:image" content={project.seo.ogImage} />
        )}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(projectSchema)}
        </script>
        <link rel="canonical" href={`https://merndigital.com/projects/${project.slug}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
        <div className="container mx-auto px-4 py-12 max-w-6xl">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-400 mb-6">
            {project.breadcrumb.map((crumb, index) => (
              <span key={index}>
                {index > 0 && <span className="mx-2">/</span>}
                <a href={crumb.url} className="hover:text-blue-400 transition-colors">
                  {crumb.name}
                </a>
              </span>
            ))}
          </nav>

          {/* Project header */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <h1 className="text-4xl font-bold text-white">{project.title}</h1>
              <a 
                href={project.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
              >
                <span>Visit Live Site</span>
                <FaExternalLinkAlt size={14} />
              </a>
            </div>
            <p className="text-xl text-gray-300 mb-4">{project.description}</p>
            
            <div className="flex flex-wrap gap-2">
              {project.category?.map((cat, idx) => (
                <span key={idx} className="px-3 py-1 bg-blue-900 text-blue-200 rounded-full text-sm">
                  {cat}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Key Details */}
          <motion.div 
            className="grid md:grid-cols-2 gap-8 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2 text-white">
                <FaCogs className="text-blue-400" />
                Project Details
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="font-medium text-gray-300">Client:</span>
                  <span className="text-white">{project.details.client}</span>
                </div>
                <div className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="font-medium text-gray-300">Type:</span>
                  <span className="text-white">{project.details.type}</span>
                </div>
                <div className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="font-medium text-gray-300">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${project.details.status === 'Live' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`}>
                    {project.details.status}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="font-medium flex items-center gap-1 text-gray-300">
                    <FaMoneyBillWave className="text-green-400" />
                    Budget:
                  </span>
                  <span className="text-white">{project.approxBudget}</span>
                </div>
                <div className="flex justify-between border-b border-gray-700 pb-2">
                  <span className="font-medium flex items-center gap-1 text-gray-300">
                    <FaCalendarAlt className="text-purple-400" />
                    Timeline:
                  </span>
                  <span className="text-white">{project.details.timeline}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-300">Technologies:</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {project.details.technologies.map((tech, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-700 text-gray-200 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {project.features?.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold mb-4 text-white">Features</h2>
                <ul className="space-y-2">
                  {project.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <FaCheck className="text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-200">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>

          {/* Pros & Cons */}
          <motion.div 
            className="grid md:grid-cols-2 gap-8 mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            {project.pros?.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold mb-4 text-green-400">Pros</h2>
                <ul className="space-y-2">
                  {project.pros.map((p, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <FaCheck className="text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-200">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {project.cons?.length > 0 && (
              <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold mb-4 text-red-400">Cons</h2>
                <ul className="space-y-2">
                  {project.cons.map((c, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <FaTimes className="text-red-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-200">{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>

          {/* Gallery */}
          {project.gallery?.length > 0 && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold mb-6 text-white">Gallery</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {project.gallery.map((img, idx) => (
                  <motion.div
                    key={idx}
                    className="overflow-hidden rounded-xl shadow-lg border border-gray-700"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.2 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <img
                      src={img}
                      alt={`${project.title} screenshot ${idx + 1}`}
                      className="w-full h-auto object-cover"
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Key Results */}
          {project.keyResults?.length > 0 && (
            <motion.div 
              className="mb-12 bg-blue-900 bg-opacity-20 p-6 rounded-xl border border-blue-800"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2 text-white">
                <FaChartLine className="text-blue-400" />
                Key Results
              </h2>
              <ul className="space-y-3">
                {project.keyResults.map((kr, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                      {idx + 1}
                    </div>
                    <span className="text-gray-200">{kr}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Testimonial */}
          {project.testimonial?.feedback && (
            <motion.div 
              className="p-8 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl border border-gray-700 mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <div className="text-2xl font-serif italic text-white mb-4">"{project.testimonial.feedback}"</div>
              <div className="text-right font-semibold text-gray-300">
                — {project.testimonial.clientName}
                {project.testimonial.position && <span className="block text-sm font-normal text-gray-400">{project.testimonial.position}</span>}
              </div>
            </motion.div>
          )}

          {/* Technical Details */}
          {project.details.briefTechStack && (
            <motion.div 
              className="bg-gray-800 p-6 rounded-xl mb-12 border border-gray-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
            >
              <h2 className="text-2xl font-semibold mb-4 text-white">Technical Implementation</h2>
              <p className="text-gray-300">{project.details.briefTechStack}</p>
            </motion.div>
          )}

          {/* Tags */}
          {project.tags?.length > 0 && (
            <motion.div 
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.5 }}
            >
              {project.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">
                  #{tag}
                </span>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectDetails;